// Date-range helpers for the analytics section (REC-88).
import { z } from 'zod';
import type { DateRange, DatePreset } from './types';

export const DEFAULT_PRESET: DatePreset = '30d';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoUtc(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Convert a quick-pick preset to a concrete from/to pair.
 * "Last N days" means an inclusive N-day window ending today, so the `from`
 * date is N-1 days ago (giving a window of size N including today). The
 * earlier off-by-one made "Last 90 days" emit a 91-day range, which exceeded
 * the API's 90-day cap and returned a 400.
 */
export function presetToRange(preset: DatePreset): { from: string; to: string } {
  const to = todayUtc();
  switch (preset) {
    case '7d':
      return { from: daysAgoUtc(6), to };
    case '30d':
      return { from: daysAgoUtc(29), to };
    case '90d':
      return { from: daysAgoUtc(89), to };
    case 'custom':
      // Caller must supply explicit from/to; fall back to 30d if missing.
      return { from: daysAgoUtc(29), to };
  }
}

/** Build a full DateRange from search params (with defaults). */
export function buildDateRange(params: {
  preset?: DatePreset;
  from?: string;
  to?: string;
}): DateRange {
  const preset: DatePreset = params.preset ?? DEFAULT_PRESET;

  if (preset === 'custom' && params.from && params.to) {
    return { preset, from: params.from, to: params.to };
  }

  const { from, to } = presetToRange(preset);
  return { preset, from, to };
}

/** Zod schema for validating analytics route search params. */
export const analyticsSearchSchema = z.object({
  preset: z.enum(['7d', '30d', '90d', 'custom']).optional(),
  from: z.string().regex(DATE_RE).optional(),
  to: z.string().regex(DATE_RE).optional(),
});

export type AnalyticsSearchInput = z.infer<typeof analyticsSearchSchema>;
